/* eslint-disable react/destructuring-assignment  */
import AddBoxIcon from '@mui/icons-material/AddBox';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import RemoveIcon from '@mui/icons-material/Remove';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import SearchIcon from '@mui/icons-material/Search';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import {Paper} from '@mui/material';
import {
  DefaultScroll,
  FixedResultPagination,
  FixedResultPaginationBig,
  LoadingSpinner,
  TranslateUtils,
  clampMin,
  getLocalStorage,
  omitNullsy,
  useApiQuery,
  useDefaultScroll,
} from '@wallaby-ui/core';
import classNames from 'classnames';
import isPlainObject from 'lodash/isPlainObject';
import throttle from 'lodash/throttle';
import MaterialTable, {MTableBodyRow, MTableCell, MaterialTableProps, Options} from 'material-table';
import React, {CSSProperties, forwardRef, useCallback, useEffect, useLayoutEffect, useMemo, useRef} from 'react';
import {useIntl} from 'react-intl';

import MaterialTableHeaderWrapper, {columnWidthPatch} from './MaterialTableHeaderWrapper';

export const DEFAULT_PAGE_SIZE = 15;
/**
 * Default readable floor (px) for variable-width (no explicit `width`) columns (PDH-1589). It is the default
 * of MaterialTableWrapper's `minColumnWidth`, so every table gets a `min-width` = sum of its column widths
 * (this floor substituted for width-less columns). The table then scrolls horizontally on small screens
 * instead of collapsing its variable columns, while staying scroll-free at normal widths. NOTE: a column/cell
 * `minWidth` is ignored under `tableLayout: 'fixed'`, so the floor must live on the `<table>` element.
 * Pass `minColumnWidth={0}` to opt a table out (e.g. tables with custom headers / cellStyle widths).
 */
export const TABLE_COLUMN_MIN_WIDTH = 120;
export function getDefaultMTOptions(pageSize: number): Options<any> {
  return {
    tableLayout: 'fixed',
    padding: 'dense',
    pageSize,
    toolbar: false,
    search: false,
    showTitle: false,
    filtering: false,
    draggable: true,
    sorting: true,
    thirdSortClick: false,
    headerStyle: {
      position: 'sticky',
      top: 0,
      zIndex: 1,
      background: 'var(--dlAgGridHeaderBackground)',
      fontFamily: 'var(--dlFontFamilySemiBold)',
      letterSpacing: 'var(--dlFontLetterSpacingNormal)',
      lineHeight: 'var(--dlFontLineHeightNormal)',
      fontSize: 'var(--dlFontSizeNormal)',
      color: 'var(--dlAgGridHeaderFontColor)',
      fontWeight: 400,
      height: 10,
      paddingInline: 11,
    },
    rowStyle: {
      width: '100%',
      height: '10px',
      overflowWrap: 'break-word',
      padding: 0,
      fontFamily: 'SourceSansPro-Regular',
    },
  };
}

export type TableSorting = {
  field?: string;
  direction?: 'asc' | 'desc' | undefined | null;
};
export type TablePaging = {
  page?: number;
  pageSize?: number;
};
type OwnProps<T extends object> = {
  getRowClass?: (data: T, index: number, level: number) => string | undefined;
  controlledSort?: TableSorting;
  controlledPage?: number;
  controlledPageSize?: number;
  variant?: 'big';
  variantPadding?: 'small';
  useMaxHeight?: boolean; // NOTE: material-table doesn't let us control the scroll pos very well
  paginationOptions?: {
    showPageSize: boolean;
    showResultCount: boolean;
    showMoveToPage: boolean;
    /** Max page-number buttons in the sliding window (default 4; e.g. document lists pass 2 for their narrow panel). */
    maxPageClickers?: number;
  };
  defaultScroll?: DefaultScroll;
  onScroll?: (this: HTMLDivElement, ev: Event) => any;
  /**
   * Persist the scroll position (BOTH axes) under this localStorage key so it survives reloads/sessions,
   * the same way individual screens persist vertical scroll today. Optional: even without it, the wrapper
   * always preserves scrollLeft/scrollTop in-memory across material-table's loading remount (page change,
   * page-size, sort), so horizontal scroll no longer resets on paging (PDM-2741).
   */
  scrollStorageId?: string;
  totalRow?: 'top';
  singleLineColumnLabels?: boolean;
  verticalColumnLabels?: boolean;
  /** Left-align the column header labels (instead of centering) so they line up with the cell data. */
  leftAlignColumnLabels?: boolean;
  /** Size the table to the sum of its column widths (table-layout: fixed) instead of stretching to 100%. */
  fitContentWidth?: boolean;
  /**
   * Readable floor (px) for variable-width (no explicit `width`) columns. The table is given a min-width
   * equal to the sum of every visible column's width (this floor substituted for width-less columns), so a
   * too-narrow container scrolls horizontally instead of collapsing the variable column. Use this instead of
   * a column `minWidth`, which `table-layout: fixed` ignores (PDH-1589).
   */
  minColumnWidth?: number;
  tinyRows?: boolean;
  cellLineHeight?: string | number;
  /** Render body cells as plain `<td>` (via the column's `render`) instead of material-table's `MTableCell`,
   * which wraps every cell in an emotion-styled MUI `TableCell`. Eliminates the dominant per-cell cost
   * (MTableCell.getStyle + emotion serialization) for large matrices. Opt-in: only safe for tables whose
   * columns all use `render` and don't rely on MTableCell features (editing, per-cell tooltips, lookups). */
  lightCells?: boolean;
  centerLoadingSpinner?: boolean;
  onTableRefSet?: (ref: React.MutableRefObject<any>) => void;
  direction?: 'desc' | 'asc' | null | undefined;
  /** Direction applied on the first click of an unsorted column (and the start of the toggle cycle). Defaults to 'asc'. */
  firstSortDirection?: 'asc' | 'desc';
};
type Props<T extends object> = OwnProps<T> & MaterialTableProps<T> & {totalPages?: number};

const materialTableIcons = {
  Add: forwardRef<SVGSVGElement, {}>((props, ref) => <AddBoxIcon {...props} ref={ref} />),
  Check: forwardRef<SVGSVGElement, {}>((props, ref) => <CheckIcon {...props} ref={ref} />),
  Clear: forwardRef<SVGSVGElement, {}>((props, ref) => <ClearIcon {...props} ref={ref} />),
  Delete: forwardRef<SVGSVGElement, {}>((props, ref) => <DeleteOutlineIcon {...props} ref={ref} />),
  DetailPanel: forwardRef<SVGSVGElement, {}>((props, ref) => <ChevronRightIcon {...props} ref={ref} />),
  Edit: forwardRef<SVGSVGElement, {}>((props, ref) => <EditIcon {...props} ref={ref} />),
  Export: forwardRef<SVGSVGElement, {}>((props, ref) => <SaveAltIcon {...props} ref={ref} />),
  Filter: forwardRef<SVGSVGElement, {}>((props, ref) => <FilterListIcon {...props} ref={ref} />),
  FirstPage: forwardRef<SVGSVGElement, {}>((props, ref) => <FirstPageIcon {...props} ref={ref} />),
  LastPage: forwardRef<SVGSVGElement, {}>((props, ref) => <LastPageIcon {...props} ref={ref} />),
  NextPage: forwardRef<SVGSVGElement, {}>((props, ref) => <ChevronRightIcon {...props} ref={ref} />),
  PreviousPage: forwardRef<SVGSVGElement, {}>((props, ref) => <ChevronLeftIcon {...props} ref={ref} />),
  ResetSearch: forwardRef<SVGSVGElement, {}>((props, ref) => <ClearIcon {...props} ref={ref} />),
  Search: forwardRef<SVGSVGElement, {}>((props, ref) => <SearchIcon {...props} ref={ref} />),
  SortArrow: forwardRef<SVGSVGElement, {}>((props, ref) => <ArrowDownwardIcon {...props} ref={ref} />),
  ThirdStateCheck: forwardRef<SVGSVGElement, {}>((props, ref) => <RemoveIcon {...props} ref={ref} />),
  ViewColumn: forwardRef<SVGSVGElement, {}>((props, ref) => <ViewColumnIcon {...props} ref={ref} />),
};

export function MaterialTableWrapper<T extends object>(props: Props<T>) {
  const {
    variant,
    variantPadding,
    useMaxHeight = false,
    columns,
    defaultScroll,
    onScroll,
    scrollStorageId,
    controlledSort,
    controlledPage,
    controlledPageSize,
    getRowClass = (_, index) => {
      return index % 2 === 0 ? 'ag-row-even' : 'ag-row-odd';
    },
    isLoading,
    totalRow = false,
    singleLineColumnLabels = false,
    verticalColumnLabels = false,
    leftAlignColumnLabels = false,
    fitContentWidth = false,
    minColumnWidth = TABLE_COLUMN_MIN_WIDTH,
    tinyRows = false,
    cellLineHeight,
    lightCells = false,
    onChangePage,
    centerLoadingSpinner = false,
    onTableRefSet,
    direction,
    firstSortDirection,
  } = props;
  // PDH-1589: floor the table width so a too-narrow container scrolls instead of collapsing variable columns.
  // table-layout: fixed ignores per-cell min-width, so the floor must be a min-width on the <table> itself.
  const tableMinWidth = useMemo(() => {
    if (minColumnWidth == null || minColumnWidth <= 0) return undefined; // 0 = opt out of the floor

    const toPx = (w: string | number | undefined) => {
      if (w == null) return null;
      const n = typeof w === 'number' ? w : parseFloat(w);
      return Number.isFinite(n) ? n : null;
    };
    return columns.reduce((acc, c) => (c.hidden ? acc : acc + (toPx(c.width) ?? minColumnWidth)), 0);
  }, [columns, minColumnWidth]);
  // table attributes // NOTE: we cannot override components.Container, otherwise scroll will be broken
  const setTableProps = useCallback(
    (paperNode: HTMLElement | null | undefined) => {
      if (!paperNode) return;
      const attributesToAdd = {
        'data-maxheight': useMaxHeight,
        'data-variant': variant,
        'data-variant-padding': variantPadding,
        'data-total-row': totalRow,
        'data-single-line-column-labels': singleLineColumnLabels,
        'data-vertical-column-labels': verticalColumnLabels,
        'data-left-align-column-labels': leftAlignColumnLabels,
        'data-fit-content-width': fitContentWidth,
        'data-tiny-rows': tinyRows,
        'data-cell-line-height': cellLineHeight,
      };
      Object.entries(attributesToAdd).forEach(([k, v]) => {
        if (v != null) {
          paperNode.setAttribute(k, String(v));
        }
      });
      const classesToAdd = ['ag-theme-balham', 'ag-body-viewport'];
      classesToAdd.forEach((v) => {
        paperNode.classList.add(v);
      });
      const stylesToAdd = {
        'display': 'block',
        'box-shadow': 'none',
      };
      Object.entries(stylesToAdd).forEach(([k, v]) => {
        paperNode.style.setProperty(k, String(v ?? ''));
      });
      if (tableMinWidth != null) {
        paperNode.style.setProperty('--dlTableMinWidth', `${tableMinWidth}px`);
      } else {
        paperNode.style.removeProperty('--dlTableMinWidth');
      }
    },
    [
      useMaxHeight,
      variant,
      variantPadding,
      totalRow,
      singleLineColumnLabels,
      verticalColumnLabels,
      leftAlignColumnLabels,
      fitContentWidth,
      tableMinWidth,
      tinyRows,
    ]
  );
  // Scroll position preservation. material-table unmounts the whole table (the LoadingSpinner early-return
  // below) on every reload — page change, page-size, sort — which destroys the scroll container and drops its
  // scrollLeft/scrollTop. We remember the last position and reapply it once the fresh container re-attaches
  // (after the spinner, on rendered data), so horizontal (and vertical) scroll survives paging on EVERY table
  // (PDM-2741). The position is ALSO persisted to localStorage so it survives a full reload: callers may pass
  // an explicit `scrollStorageId`, but when they don't we derive a stable one from the route + the table's
  // column signature — so report/detail tables keep their scroll on reload too, without per-call-site wiring.
  const persistScrollStorageId =
    scrollStorageId ??
    (typeof window !== 'undefined' ? `/mt-scroll:${window.location.pathname}:${columns.map((c) => c.field ?? '').join(',')}` : undefined);
  const _mtRef = useRef(null as Record<string, any> | null);
  const scrollRef = useRef(null as HTMLDivElement | null);
  const scrollPos = useRef<[number, number]>(
    persistScrollStorageId ? getLocalStorage<[number, number]>(persistScrollStorageId, [0, 0]) : [0, 0]
  );
  const persistScroll = useMemo(
    () =>
      throttle((x: number, y: number) => {
        if (persistScrollStorageId) {
          try {
            localStorage.setItem(persistScrollStorageId, JSON.stringify([x, y]));
          } catch {
            // ignore quota / unavailable localStorage
          }
        }
      }, 250),
    [persistScrollStorageId]
  );
  const handleScroll = useCallback(
    function handleScrollInner(this: HTMLDivElement, ev: Event) {
      const el = ev.target as HTMLDivElement;
      scrollPos.current = [el.scrollLeft, el.scrollTop];
      persistScroll(el.scrollLeft, el.scrollTop);
      onScroll?.call(this, ev);
    },
    [onScroll, persistScroll]
  );
  const prevHandleScroll = useRef(undefined as ((this: HTMLDivElement, ev: Event) => any) | undefined);
  const setPaperRef = useCallback(
    (mtRef: {tableContainerDiv: React.RefObject<HTMLDivElement>} | null) => {
      _mtRef.current = mtRef;
      onTableRefSet?.(mtRef);
      const scrollNode = mtRef?.tableContainerDiv.current;
      const scrollWrapper = (scrollNode ?? null) as HTMLDivElement | null;
      if (scrollWrapper) {
        if (prevHandleScroll.current) scrollRef.current?.removeEventListener('scroll', prevHandleScroll.current);
        scrollWrapper.addEventListener('scroll', handleScroll);
        prevHandleScroll.current = handleScroll;
      }
      scrollRef.current = scrollWrapper;
      if (scrollNode) {
        setTableProps(scrollNode.parentElement?.parentElement?.parentElement);
      }
    },
    [handleScroll, onTableRefSet, setTableProps]
  );
  // Restore scroll on (re)mount and after each page change. material-table unmounts the whole table on every
  // reload, so `useDefaultScroll` is re-armed via `dependOn` = the page — it then polls until the spinner is
  // gone and the data is laid out before scrolling, which fixes the timing a bare rAF can't (the fresh table
  // has no width yet at mount, so an early scrollTo clamps to 0). `scrollPos` holds the live position (seeded
  // from localStorage when `scrollStorageId` is set), so BOTH axes are preserved across paging (PDM-2741).
  // Re-arm the restore after ANY reload/page change so horizontal scroll survives paging on EVERY table,
  // regardless of which paging props the call-site happens to use: some pass `page`, some `controlledPage`,
  // reports re-fetch (toggling `isLoading`) and page client-side, page-size and sort also remount. We also
  // fold in whatever `dependOn` the caller supplied (e.g. a document list keys its restore on the selected
  // documentId). One primitive string so useDefaultScroll compares it by value and re-fires only on change.
  const sortKey = `${controlledSort?.field ?? ''}:${controlledSort?.direction ?? ''}`;
  const restoreScroll: DefaultScroll = {
    toX: scrollPos.current[0] || defaultScroll?.toX,
    to: scrollPos.current[1] || defaultScroll?.to,
    dependOn: [defaultScroll?.dependOn ?? '', props.page ?? '', controlledPage ?? '', controlledPageSize ?? '', isLoading ? 1 : 0, sortKey].join(
      '|'
    ),
    debug: defaultScroll?.debug,
  };
  useDefaultScroll(scrollRef, restoreScroll);
  // controlled sort
  const onOrderChangeRef = useRef((_index: number, _direction: 'asc' | 'desc' | '') => {
    // noop
  });
  useEffect(() => {
    const controlledSortIndex = columns.findIndex((column) => column.field && column.field === controlledSort?.field);
    const controlledSortExists = controlledSortIndex !== -1 && controlledSort?.direction;
    if (controlledSortExists) {
      onOrderChangeRef.current(controlledSortIndex, controlledSort?.direction ?? '');
    }
    // eslint-disable-next-line
  }, [controlledSort?.field, controlledSort?.direction]);
  // controlled page
  useEffect(() => {
    if (props.options?.paging === false || controlledPage == null) return;
    const mtRefValue = _mtRef.current;
    if (mtRefValue && mtRefValue.state.currentPage !== controlledPage) {
      mtRefValue.onChangePage(null, controlledPage);
    }
  });
  // controlled pageSize
  const onRowsPerPageChangeRef = useRef((_event: {target: {value: number}}) => {
    // noop
  });
  useEffect(() => {
    if (controlledPageSize) {
      onRowsPerPageChangeRef.current({target: {value: controlledPageSize}});
    }
  }, [controlledPageSize]);
  const isInitialLoad = useRef(true);
  useEffect(() => {
    isInitialLoad.current = false;
  }, []);
  const userComponents = props.components;
  if (isLoading) {
    const wrapperStyles: CSSProperties = centerLoadingSpinner ? {flexGrow: 1, alignContent: 'center'} : {};
    return <LoadingSpinner isVisible marginTop={0} wrapperStyles={wrapperStyles} />;
  }

  return (
    <MaterialTable
      {...props}
      icons={materialTableIcons}
      onChangePage={(page, pageSize) => {
        if (onChangePage && !isInitialLoad.current) onChangePage(page, pageSize);
        isInitialLoad.current = false;
      }}
      localization={{
        body: {
          emptyDataSourceMessage: <NoRowsToShow />,
        },
      }}
      tableRef={setPaperRef}
      components={{
        ...userComponents,
        // NOTE: we cannot override components.Container, otherwise scroll will be broken
        // eslint-disable-next-line
        Header: (headerProps) => {
          onOrderChangeRef.current = headerProps.onOrderChange;
          if (userComponents?.Header) {
            const UserHeader = userComponents.Header as any;
            return <UserHeader {...headerProps} />;
          }
          return (
            <MaterialTableHeaderWrapper
              {...headerProps}
              orderDirection={direction ?? headerProps.orderDirection}
              firstSortDirection={firstSortDirection}
            />
          );
        },
        // eslint-disable-next-line
        Row: (_props) =>
          _props.data && _props.data[LAZY_FOOTER_KEY] ? (
            <LazyFooterRow colSpan={columns.filter((c) => !c.hidden).length || 1} />
          ) : (
            <MTRowWithGetRowClass {..._props} getRowClass={getRowClass} />
          ),
        // eslint-disable-next-line
        Cell: (_props) =>
          lightCells ? (
            <LightCell {..._props} cellLineHeight={cellLineHeight} />
          ) : (
            <MTCellWrapper {..._props} cellLineHeight={cellLineHeight} />
          ),
        // eslint-disable-next-line
        Pagination: (_props) => {
          onRowsPerPageChangeRef.current = _props.onRowsPerPageChange;
          return <MTPaginationWrapper {..._props} paginationOptions={props.paginationOptions} variant={variant} />;
        },
      }}
    />
  );
}
MaterialTableWrapper.displayName = 'MaterialTableWrapper';

/**
 * Marker key on a data row that tells MaterialTableWrapper to render it as a full-width loading
 * sentinel instead of a normal row. Used by LazyMaterialTable for incremental ("load more on scroll")
 * rendering: the sentinel sits in the table body, so it scrolls with the content AND is reflected in
 * the scrollbar (the user sees there is more data).
 */
export const LAZY_FOOTER_KEY = '__lazyFooter';

function LazyFooterRow({colSpan}: {colSpan: number}) {
  return (
    <tr data-name='lazy-footer-row'>
      <td colSpan={colSpan} style={{padding: 0, border: 'none'}}>
        {/* Clip the (horizontally re-centered) sentinel: LazyMaterialTable applies a translateX to center the
            spinner in the visible viewport, and a CSS transform CONTRIBUTES to scrollable overflow — without
            this clip it extended the table's scrollWidth, adding blank space to the right of the last column
            when scrolled right. */}
        <div style={{overflow: 'hidden'}}>
          {/* data-lazy-sentinel is the scroll-position target re-centered by LazyMaterialTable.
              An inline spinner (NOT the full-page <LoadingSpinner>, which is a fixed 125px icon that made
              a ~141px loading row whose appearing/disappearing visibly resized the scrollbar). Sized for
              visibility but well below 125px to keep the loading row compact. */}
          <div data-lazy-sentinel style={{display: 'flex', justifyContent: 'center', padding: '8px 0'}}>
            <span className='fas fa-circle-notch fa-spin' style={{fontSize: 72, color: 'var(--dlLoadingSpinnerTextColor)'}} />
          </div>
        </div>
      </td>
    </tr>
  );
}

export const NoRowsToShow: React.FC = () => {
  const intl = useIntl();
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        padding: '40px 8px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 8,
        display: 'inline-flex',
      }}
    >
      <div
        style={{
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 16,
          display: 'inline-flex',
        }}
      >
        <div
          style={{
            width: 130,
            textAlign: 'center',
            color: '#121619',
            fontSize: 14,
            fontFamily: 'SourceSansPro-Semibold',
            fontWeight: 400,
            wordWrap: 'break-word',
          }}
        >
          {TranslateUtils.translate('agGrid.noRowsToShow', intl)}
        </div>
      </div>
    </div>
  );
};

type MTRowWithGetRowClassProps<T extends object> = OwnProps<T> & any;
function MTRowWithGetRowClass<T extends object>(props: MTRowWithGetRowClassProps<T>) {
  const {getRowClass, data, index, level, class: _class} = props;
  const propsWithoutGetRowClass = {...props};
  delete propsWithoutGetRowClass.getRowClass;
  return (
    <MTableBodyRow
      {...propsWithoutGetRowClass}
      className={classNames(_class, 'MuiTableRow-root', getRowClass && getRowClass(data, index, level))}
      role='row'
      row-index={index}
    />
  );
}

function MTCellWrapper(props: any) {
  const {columnDef, cellLineHeight, style, ...restProps} = props;
  const accProps = {...restProps, columnDef: columnWidthPatch(columnDef)};

  const cellStyle = {
    ...style,
    ...(cellLineHeight && {lineHeight: cellLineHeight}),
  };

  return <MTableCell {...accProps} style={cellStyle} role='gridcell' col-id={columnDef.field} />;
}

// Plain-`<td>` body cell used when `lightCells` is on. Replaces material-table's `MTableCell` (which wraps
// every cell in an emotion-styled MUI `TableCell` — the dominant per-cell render cost). The visual box
// (padding/box-sizing/vertical-align, NO border — matching MTableCell-sizeSmall) comes from a single static
// stylesheet injected once, so there is no per-cell emotion serialization. Column widths are NOT set here:
// the table is `table-layout: fixed`, so widths come from the header row; body-cell widths are ignored.
const LIGHT_CELL_STYLE_ID = 'dl-light-cell-style';
function ensureLightCellStyle() {
  if (typeof document === 'undefined' || document.getElementById(LIGHT_CELL_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = LIGHT_CELL_STYLE_ID;
  // Doubled class (specificity 0,2,1) so it beats the ag-grid compatibility rule
  style.textContent =
    'td.dl-light-cell.dl-light-cell{box-sizing:border-box;padding:2px 12px;vertical-align:middle;border:0;' +
    'text-align:left;font-size:14px;line-height:1.43;color:inherit;font-family:inherit;}';
  document.head.appendChild(style);
}

function LightCell(props: any) {
  const {columnDef, rowData, value, cellLineHeight} = props;
  ensureLightCellStyle();
  const content = typeof columnDef.render === 'function' ? columnDef.render(rowData, 'row') : value ?? rowData?.[columnDef.field];
  const style: CSSProperties = {};
  if (columnDef.align) style.textAlign = columnDef.align;
  if (cellLineHeight) style.lineHeight = cellLineHeight;
  return (
    <td
      className={classNames('dl-light-cell', columnDef.cellClassName)}
      style={Object.keys(style).length ? style : undefined}
      role='gridcell'
      col-id={columnDef.field}
    >
      {content}
    </td>
  );
}

function MTPaginationWrapper(props: any) {
  const {variant, count, onPageChange, onRowsPerPageChange, rowsPerPage, paginationOptions} = props;
  let {page} = props;
  if (Number.isNaN(page)) page = 0;
  const intl = useIntl();

  const maxPages = Math.ceil(count / rowsPerPage || 1);
  const countInPage = Math.min(count - page * rowsPerPage, rowsPerPage);
  const goToPage = useCallback(
    (newPage: number) => {
      onPageChange({target: {value: newPage}}, newPage - 1);
    },
    [onPageChange]
  );
  const changeRowsPerPage = useCallback(
    (newRowsPerPage: number) => {
      onRowsPerPageChange({target: {value: newRowsPerPage}});
    },
    [onRowsPerPageChange]
  );

  const PaginationComponent = variant === 'big' ? FixedResultPaginationBig : FixedResultPagination;

  // material-table renders the pagination in a SEPARATE footer <table> that is a sibling OUTSIDE the scrollable
  // data-table container, yet it still matches `.ag-body-viewport .MuiTable-root` and so inherited the
  // `--dlTableMinWidth` floor meant for the data table — forcing it to the (wide) data-table width and pushing
  // the right-aligned control off-screen on wide tables / narrow panels. Neutralise that on the footer table
  // itself (inline beats the stylesheet): full card width, no floor, so the control right-aligns within the
  // visible card and is clipped on the left only when there is genuinely no room (PDM-2741).
  const tdRef = useRef<HTMLTableCellElement>(null);
  useLayoutEffect(() => {
    const footerTable = tdRef.current?.closest('table') as HTMLTableElement | null;
    if (footerTable) {
      footerTable.style.minWidth = '0';
      footerTable.style.width = '100%';
    }
  });

  return (
    // overflow:hidden makes the pagination's min-content width 0, so the footer table stays the width of the
    // visible card instead of stretching the control off-screen; combined with the control's right alignment
    // (justify-content:flex-end) it stays single-line, shifts left into available space as the panel narrows,
    // and is clipped on the LEFT once the card is narrower than the control — acceptable (PDM-2741).
    <td ref={tdRef} style={{width: '100%', display: 'block', overflow: 'hidden'}}>
      <div className='MuiToolbar-root MuiTablePagination-toolbar' style={{marginTop: 6, overflow: 'hidden'}}>
        <PaginationComponent
          activePage={page + 1}
          activeSize={rowsPerPage}
          countInPage={countInPage}
          handlePageChange={goToPage}
          handlePageSizeChange={changeRowsPerPage}
          pages={maxPages}
          pageSize={rowsPerPage}
          resultCount={count}
          showPageSize={paginationOptions?.showPageSize ?? true}
          showResultCount={paginationOptions?.showResultCount ?? true}
          showMoveToPage={paginationOptions?.showMoveToPage ?? true}
          maxPageClickers={paginationOptions?.maxPageClickers}
          intl={intl}
        />
      </div>
    </td>
  );
}

type TableData<T> = {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
  countOnPage: number | undefined;
  totalCount: number | undefined;
  page: number | undefined;
  totalPages: number | undefined;
  refetch: () => void;
};
export function useTableData<P, T>(url: string, apiParams: P | URLSearchParams, objectKey = 'content', enabled = true): TableData<T> {
  const params = apiParams instanceof URLSearchParams ? apiParams : new URLSearchParams(omitNullsy(apiParams));
  const {data, isLoading, isError, refetch} = useApiQuery<any>([url, String(params)], null, {
    axiosConfig: {
      url,
      params,
    },
    reactQueryOptions: {
      enabled,
      staleTime: 0,
      refetchOnMount: 'always',
    },
  });
  return {
    data: Array.isArray(data) ? data : data?.[objectKey] ?? data,
    isLoading,
    isError,
    countOnPage: data?.size ?? undefined,
    totalCount: data?.totalElements ?? undefined,
    page: data?.number ?? undefined,
    totalPages: data?.totalPages ?? undefined,
    refetch,
  };
}
export function useTableParams<T>(
  params: T,
  sorting?: TableSorting,
  sortingOverrides: Record<string, string | string[]> = {}
): URLSearchParams {
  const accParams = new URLSearchParams(omitNullsy(params));
  if (!sorting || !sorting.field || !sorting.direction) return accParams;
  if (sorting.field in sortingOverrides) {
    const override = sortingOverrides[sorting.field];
    if (Array.isArray(override)) {
      override.forEach((k) => {
        accParams.append('sort', `${k},${sorting.direction}`);
      });
    } else {
      accParams.append('sort', `${override},${sorting.direction}`);
    }
  } else {
    accParams.append('sort', `${sorting.field},${sorting.direction}`);
  }
  return accParams;
}
export function clampPageNumber(
  page: number | undefined | null,
  pageSize: number | undefined | null,
  totalCount: number | undefined | null
): number {
  const totalCountMinusOne = totalCount != null ? totalCount - 1 : 0; // eslint-disable-line
  const maxPage = Math.floor(totalCountMinusOne / (pageSize || DEFAULT_PAGE_SIZE));
  return clampMin(page ?? 0, 0, maxPage);
}
export function flattenTableParams<T extends object>(params: T, prefix: string = ''): Record<string, unknown> {
  return Object.entries(params).reduce((acc, [key, value]) => {
    if (value === null || value === undefined) return acc;
    const newKey = prefix ? `${prefix}.${key}` : key;
    if (isPlainObject(value)) {
      return Object.assign(acc, flattenTableParams(value, newKey));
    }
    return Object.assign(acc, {[newKey]: value});
  }, {} as Record<string, unknown>);
}
